import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { searchCourses, getCourseDetails } from '@/integrations/golfcourseapi/client';
import { importCourse } from '@/integrations/supabase/courseImportService';
import { findCourseByApiId } from '@/integrations/supabase/courseService';
import { searchCourseCache, upsertCourseCache, cachedToApiSearchResult } from '@/integrations/supabase/courseCacheService';
import { addCourseToUser } from '@/integrations/supabase/profileCourseService';
import { autoMapTees } from '@/utils/teeMapping';
import type { GolfCourseApiSearchResult, GolfCourseApiCourse, TeeMapping } from '@/types/golfCourseApi';
import type { TeeBox } from '@/types';

export type ImportStep = 'search' | 'preview' | 'mapping' | 'importing' | 'done';

export function useCourseImport() {
  const { user } = useAuth();

  const [step, setStep] = useState<ImportStep>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GolfCourseApiSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<GolfCourseApiCourse | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [teeMappings, setTeeMappings] = useState<TeeMapping[]>([]);
  const [importedCourseId, setImportedCourseId] = useState<string | null>(null);

  // Debounced search: cache-first, API fallback
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setSearching(true);

      // 1. Search cache first (fast, local Postgres)
      const cached = await searchCourseCache(searchQuery.trim());
      const cacheResults = cached.map(cachedToApiSearchResult);

      if (cancelled) return;

      // 2. If cache has enough results, skip the external API
      if (cacheResults.length >= 10) {
        setSearchResults(cacheResults);
        setSearching(false);
        return;
      }

      // 3. Cache is sparse — also hit the external API
      const apiResults = await searchCourses(searchQuery);

      if (cancelled) return;

      // 4. Fire-and-forget: grow the cache with API results
      upsertCourseCache(apiResults);

      // 5. Merge: API results take priority, append cache-only results (dedup by id)
      const apiIds = new Set(apiResults.map(r => r.id));
      const cacheOnly = cacheResults.filter(r => !apiIds.has(r.id));
      setSearchResults([...apiResults, ...cacheOnly]);
      setSearching(false);
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  const selectSearchResult = useCallback(async (result: GolfCourseApiSearchResult) => {
    if (!user?.id) return;

    setLoadingDetails(true);

    // Dedup: check if this API course already exists in our DB
    const existing = await findCourseByApiId(result.id);
    if (existing) {
      await addCourseToUser(user.id, existing.id, true);
      setLoadingDetails(false);
      setImportedCourseId(existing.id);
      toast({
        title: 'Course Added',
        description: `${existing.name} is already in our database — added to your courses.`,
      });
      setStep('done');
      return;
    }

    const details = await getCourseDetails(result.id);
    setLoadingDetails(false);

    if (!details) {
      toast({
        title: 'Error',
        description: 'Could not load course details. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    setSelectedCourse(details);
    setStep('preview');
  }, [user?.id]);

  const proceedToMapping = useCallback(() => {
    if (!selectedCourse?.tees) return;
    const mappings = autoMapTees(selectedCourse.tees);
    setTeeMappings(mappings);
    setStep('mapping');
  }, [selectedCourse]);

  const updateTeeMapping = useCallback((index: number, color: TeeBox | null) => {
    setTeeMappings(prev => prev.map((m, i) =>
      i === index ? { ...m, dbColor: color } : m
    ));
  }, []);

  const doImport = useCallback(async () => {
    if (!selectedCourse || !user?.id) return;

    setStep('importing');
    const result = await importCourse({
      apiCourse: selectedCourse,
      teeMappings,
      userId: user.id,
    });

    if (result.error && !result.courseId) {
      toast({
        title: 'Import Failed',
        description: result.error,
        variant: 'destructive',
      });
      setStep('mapping'); // Let user retry
      return;
    }

    if (result.courseId) {
      setImportedCourseId(result.courseId);
    }

    if (result.error && result.courseId) {
      toast({
        title: 'Partial Import',
        description: result.error,
      });
    } else {
      toast({
        title: 'Course Imported',
        description: `${selectedCourse.course_name || selectedCourse.club_name} has been added to your courses.`,
      });
    }

    setStep('done');
  }, [selectedCourse, teeMappings, user?.id]);

  const reset = useCallback(() => {
    setStep('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedCourse(null);
    setLoadingDetails(false);
    setTeeMappings([]);
    setImportedCourseId(null);
  }, []);

  const goBack = useCallback(() => {
    if (step === 'preview') setStep('search');
    else if (step === 'mapping') setStep('preview');
  }, [step]);

  return {
    step,
    searchQuery,
    setSearchQuery,
    searchResults,
    searching,
    selectSearchResult,
    selectedCourse,
    loadingDetails,
    proceedToMapping,
    teeMappings,
    updateTeeMapping,
    doImport,
    reset,
    goBack,
    importedCourseId,
  };
}
